/**
 * Origin address card for the domain-detail page — mirrors the vendor's "Origin
 * address" editor: address type, a dynamic source list, the load-balancer
 * method, and the global-fallback address. Read view → Edit → form with Save.
 *
 * Origin persists via PATCH /waf/domains/settings ({ origin }); the fallback
 * address has its own endpoints (PATCH/DELETE /waf/domains/global-fallback).
 * Before saving, a pre-check (POST /waf/domains/validate-origin-fallback) warns
 * if the change would route traffic into fallback.
 */
import { useState } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
  IconButton,
  Input,
  SegmentedControl,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useUpdateWafDomainSettings,
  useSetGlobalFallback,
  useClearGlobalFallback,
  useValidateOriginFallback,
  wafExtractError,
  type DomainOrigin,
  type WafDomainOriginUpdate,
} from '@/api/waf';
import styles from './WafDomainDetail.module.css';

const TYPES = [
  { value: 'FQDN', label: 'FQDN' },
  { value: 'IPv4', label: 'IPv4' },
];

/** Load-balancer methods (vendor `balanceMode`). */
const BALANCE_MODES = [
  { value: 'consistent', label: 'IP hash' },
  { value: 'roundrobin', label: 'Round-robin' },
  { value: 'failover', label: 'Failover' },
];
const balanceLabel = (m?: string) => BALANCE_MODES.find((b) => b.value === m)?.label ?? '—';

/** Dotted-quad IPv4 (each octet 0–255). */
const IPV4_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
/** A DNS hostname (≥2 dot-separated labels, alpha TLD). */
const FQDN_RE = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

const muted: React.CSSProperties = { color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' };
const heading: React.CSSProperties = { fontWeight: 'var(--vn-font-weight-medium)', fontSize: 'var(--vn-font-size-sm)' };
const divider: React.CSSProperties = { height: 1, background: 'var(--vn-border-subtle)', margin: 'var(--vn-space-1) 0' };

/** A label-above-control cell. */
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <span style={heading}>{label}</span>
      {hint ? <span style={muted}>{hint}</span> : null}
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <div style={{ color: 'var(--vn-fg-muted)' }}>{label}</div>
      <div style={{ wordBreak: 'break-word' }}>{value ?? '—'}</div>
    </>
  );
}

export function OriginAddressCard({
  serviceId,
  domain,
  origin,
  globalFallback,
}: {
  serviceId: string;
  domain: string;
  origin: DomainOrigin;
  globalFallback?: string;
}) {
  const { show } = useToast();
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Whether the pre-check says this change routes the domain into fallback —
  // only tailors the confirm copy; the confirm always shows.
  const [willFallback, setWillFallback] = useState(false);

  const [type, setType] = useState(origin.type || 'FQDN');
  const [sources, setSources] = useState<string[]>(origin.sources?.length ? origin.sources : ['']);
  const [balanceMode, setBalanceMode] = useState(origin.balance_mode || 'consistent');
  const [fallback, setFallback] = useState(globalFallback ?? '');

  const settingsMut = useUpdateWafDomainSettings(serviceId, domain);
  const setFb = useSetGlobalFallback(serviceId, domain);
  const clearFb = useClearGlobalFallback(serviceId, domain);
  const validateMut = useValidateOriginFallback(serviceId, domain);
  const pending = settingsMut.isPending || setFb.isPending || clearFb.isPending || validateMut.isPending;

  const startEdit = () => {
    setType(origin.type || 'FQDN');
    setSources(origin.sources?.length ? origin.sources : ['']);
    setBalanceMode(origin.balance_mode || 'consistent');
    setFallback(globalFallback ?? '');
    setEditing(true);
  };

  const setSource = (i: number, v: string) => setSources((prev) => prev.map((s, idx) => (idx === i ? v : s)));
  const addSource = () => setSources((prev) => [...prev, '']);
  const removeSource = (i: number) =>
    setSources((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));

  // Per-source format error for the selected address type (null = ok/empty).
  const sourceError = (s: string): string | null => {
    const t = s.trim();
    if (!t) return null;
    if (type === 'IPv4') return IPV4_RE.test(t) ? null : 'Invalid IPv4 address';
    return FQDN_RE.test(t) ? null : 'Invalid FQDN';
  };

  const cleanSources = sources.map((s) => s.trim()).filter(Boolean);
  const valid = cleanSources.length > 0 && cleanSources.every((s) => !sourceError(s));

  /** Persist origin + fallback. */
  const persist = async () => {
    const originPatch: WafDomainOriginUpdate = { type, sources: cleanSources, balance_mode: balanceMode };
    try {
      await settingsMut.mutateAsync({ origin: originPatch });
      const fb = fallback.trim();
      if (fb) await setFb.mutateAsync({ ip: fb });
      else if (globalFallback) await clearFb.mutateAsync();
      show({ tone: 'success', title: 'Origin updated' });
      setConfirmOpen(false);
      setEditing(false);
    } catch (e) {
      setConfirmOpen(false);
      show({ tone: 'danger', title: wafExtractError(e, 'Update failed') });
    }
  };

  /** Pre-check first, then always confirm; persist only after the user confirms. */
  const save = async () => {
    if (!valid) return;
    try {
      const isFallback = await validateMut.mutateAsync({
        origin: { type, sources: cleanSources, balance_mode: balanceMode },
        global_fallback: fallback.trim(),
      });
      setWillFallback(isFallback);
      setConfirmOpen(true);
    } catch (e) {
      show({ tone: 'danger', title: wafExtractError(e, 'Validation failed') });
    }
  };

  return (
    <Card>
      <CardHeader style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <CardTitle>Origin address</CardTitle>
        {!editing && (
          <IconButton variant="ghost" size="sm" aria-label="Edit origin" onClick={startEdit}>
            <Pencil size={14} />
          </IconButton>
        )}
      </CardHeader>
      <CardBody>
        <p style={{ ...muted, marginTop: 0, marginBottom: 'var(--vn-space-4)' }}>
          Direct this domain to an IP address or an FQDN as its origin. With multiple origins,
          requests are load-balanced by the selected method.
        </p>

        {!editing ? (
          <div className={styles.detailGrid}>
            <Row label="Address type" value={origin.type || '—'} />
            <Row label="Origin address" value={origin.sources?.length ? origin.sources.join(', ') : '—'} />
            <Row label="Load balancer" value={balanceLabel(origin.balance_mode)} />
            <Row label="Fallback address" value={globalFallback || '—'} />
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 760 }}>
            <div className={styles.editGrid}>
              <Field label="Address type">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-2)' }}>
                  <div style={{ alignSelf: 'flex-start' }}>
                    <SegmentedControl items={TYPES} value={type} onValueChange={setType} aria-label="Address type" />
                  </div>
                  {sources.map((s, i) => {
                    const err = sourceError(s);
                    return (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                          <Input
                            style={{ flex: '1 1 auto', minWidth: 0 }}
                            placeholder={type === 'FQDN' ? 'origin.example.com' : '203.0.113.4'}
                            value={s}
                            onChange={(e) => setSource(i, e.target.value)}
                            disabled={pending}
                            aria-label={`Origin address ${i + 1}`}
                            aria-invalid={!!err}
                          />
                          <IconButton
                            variant="ghost"
                            size="sm"
                            aria-label="Remove address"
                            onClick={() => removeSource(i)}
                            disabled={pending || sources.length <= 1}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </div>
                        {err && (
                          <span style={{ color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-sm)' }}>{err}</span>
                        )}
                      </div>
                    );
                  })}
                  <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addSource} disabled={pending}>
                    Add address
                  </Button>
                </div>
              </Field>

              <Field label="Load Balancer Methods" hint="How requests are spread across multiple origins.">
                <div style={{ alignSelf: 'flex-start' }}>
                  <SegmentedControl
                    items={BALANCE_MODES}
                    value={balanceMode}
                    onValueChange={setBalanceMode}
                    aria-label="Load balancer method"
                  />
                </div>
              </Field>
            </div>

            <div style={divider} />

            <Field label="Fallback address" hint="Routed to when no CDN is available. Leave blank to clear.">
              <Input placeholder="FQDN or IP address" value={fallback} onChange={(e) => setFallback(e.target.value)} disabled={pending} />
            </Field>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-2)' }}>
              <Button variant="ghost" onClick={() => setEditing(false)} disabled={pending}>
                Cancel
              </Button>
              <Button onClick={save} disabled={pending || !valid}>
                Save
              </Button>
            </div>
          </div>
        )}
      </CardBody>

      {confirmOpen && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmOpen(false); }}
          title={willFallback ? 'Route traffic to fallback?' : 'Save origin changes?'}
          description={
            willFallback
              ? 'This origin change will put the domain into origin fallback — traffic may bypass the CDN. Continue?'
              : 'Apply these origin changes to this domain?'
          }
          confirmLabel={willFallback ? 'Save anyway' : 'Save'}
          destructive={willFallback}
          loading={settingsMut.isPending || setFb.isPending || clearFb.isPending}
          onConfirm={persist}
        />
      )}
    </Card>
  );
}

export default OriginAddressCard;
